'use client';

import { c as _c } from "react/compiler-runtime";
import { type RefObject, useEffect } from 'react';

/**
 * A custom hook that adds a global search shortcut functionality.
 * Port of dapp-ui-v5 useGlobalSearchShortcut.
 *
 * @param inputRef - A ref object pointing to the input element to focus.
 * @param isGlobalSearchInput - A boolean indicating whether this input is the global search input.
 *
 * @description
 * This hook adds a keydown event listener to the document that listens for the 'Ctrl+F' or 'Cmd+F' key combination.
 * When triggered, it prevents the default browser search behavior and focuses the provided input element instead.
 * The event listener is only added if isGlobalSearchInput is true.
 *
 * @example
 * const inputRef = useRef<HTMLInputElement>(null);
 * useGlobalSearchShortcut(inputRef, true);
 */
function useGlobalSearchShortcut(inputRef, isGlobalSearchInput) {
  const $ = _c(4);
  let t0;
  let t1;
  if ($[0] !== inputRef || $[1] !== isGlobalSearchInput) {
    t0 = () => {
      if (!isGlobalSearchInput) {
        return;
      }
      const handleKeyDown = event => {
        if ((event.ctrlKey || event.metaKey) && event.key === "f") {
          event.preventDefault();
          inputRef.current?.focus();
        }
        if (event.key === "Escape") {
          inputRef.current?.blur();
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    };
    t1 = [isGlobalSearchInput, inputRef];
    $[0] = inputRef;
    $[1] = isGlobalSearchInput;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
}
export default useGlobalSearchShortcut;